import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getVendorStats } from '../../redux/slices/vendorSlice';
import { Package, ShoppingCart, DollarSign, Clock, TrendingUp, AlertCircle, AlertTriangle, X, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = import.meta.env.VITE_BACKEND_URL;

const VendorDashboard = () => {
  const dispatch = useDispatch();
  const { stats, loading, error } = useSelector((state) => state.vendor);
  const { userInfo } = useSelector((state) => state.auth);
  
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(getVendorStats());
  }, [dispatch]);

  const handleReportIssue = async () => {
    if (!reportData.subject.trim() || !reportData.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/vendors/report-issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
        },
        body: JSON.stringify({
          subject: reportData.subject,
          message: reportData.message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit report');
      }

      toast.success('Issue reported successfully! We\'ll get back to you soon.');
      setShowReportModal(false);
      setReportData({ subject: '', message: '' });
    } catch (err) {
      console.error('Report issue error:', err);
      toast.error(err.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: `₹${stats?.totalRevenue?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: 'bg-green-100 text-green-600',
      link: '/vendor/orders',
    },
    {
      title: 'Total Orders',
      value: stats?.totalOrders || 0,
      icon: ShoppingCart,
      color: 'bg-blue-100 text-blue-600',
      link: '/vendor/orders',
    },
    {
      title: 'Total Products',
      value: stats?.totalProducts || 0,
      icon: Package,
      color: 'bg-purple-100 text-purple-600',
      link: '/vendor/products',
    },
    {
      title: 'Pending Orders',
      value: stats?.pendingOrders || 0,
      icon: Clock,
      color: 'bg-orange-100 text-orange-600',
      link: '/vendor/orders?status=pending',
    },
  ];

  const earningsCards = [
    {
      title: 'Total Earnings',
      value: `₹${stats?.totalEarnings?.toLocaleString() || 0}`,
      subtitle: 'After commission',
      color: 'bg-gradient-to-br from-green-500 to-green-600',
    },
    {
      title: 'Platform Commission',
      value: `₹${stats?.totalCommission?.toLocaleString() || 0}`,
      subtitle: 'Total deducted',
      color: 'bg-gradient-to-br from-gray-500 to-gray-600',
    },
    {
      title: 'Available Balance',
      value: `₹${stats?.availableBalance?.toLocaleString() || 0}`,
      subtitle: 'Ready for payout',
      color: 'bg-gradient-to-br from-blue-500 to-blue-600',
    },
  ];

  return (
    <div className="max-w-7xl mx-auto p-6 min-h-screen">
      {/* Header with Report Issue Button */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Vendor Dashboard</h1>
          <p className="text-gray-600">Welcome back! Here's your store overview.</p>
        </div>
        <button
          onClick={() => setShowReportModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition"
        >
          <AlertTriangle className="w-5 h-5" />
          Report an Issue
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link
              key={index}
              to={stat.link}
              className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-full ${stat.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-gray-600 text-sm mb-1">{stat.title}</h3>
              <p className="text-2xl font-bold">{stat.value}</p>
            </Link>
          );
        })}
      </div>

      {/* Earnings Overview */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4">Earnings Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {earningsCards.map((card, index) => (
            <div
              key={index}
              className={`${card.color} text-white p-6 rounded-lg shadow`}
            >
              <h3 className="text-white/80 text-sm mb-2">{card.title}</h3>
              <p className="text-3xl font-bold mb-1">{card.value}</p>
              <p className="text-white/70 text-sm">{card.subtitle}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Revenue */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">Recent Revenue (30 days)</h3>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-green-600">
            ₹{stats?.recentRevenue?.toLocaleString() || 0}
          </p>
          <p className="text-gray-600 text-sm mt-2">Revenue from last 30 days</p>
        </div>

        {/* Pending Products */}
        {stats?.pendingProducts > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Pending Approvals</h3>
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            </div>
            <p className="text-3xl font-bold text-yellow-600">
              {stats?.pendingProducts}
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Products awaiting admin approval
            </p>
            <Link
              to="/vendor/products?status=pending"
              className="text-yellow-700 font-medium text-sm mt-3 inline-block hover:underline"
            >
              View pending products →
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="font-bold text-lg mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link
            to="/vendor/products/new"
            className="text-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-black hover:bg-gray-50 transition"
          >
            <Package className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="font-medium">Add Product</p>
          </Link>

          <Link
            to="/vendor/orders"
            className="text-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-black hover:bg-gray-50 transition"
          >
            <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="font-medium">View Orders</p>
          </Link>

          <Link
            to="/vendor/payouts"
            className="text-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-black hover:bg-gray-50 transition"
          >
            <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="font-medium">Request Payout</p>
          </Link>

          <Link
            to="/vendor/profile"
            className="text-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-black hover:bg-gray-50 transition"
          >
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="font-medium">Update Profile</p>
          </Link>
        </div>
      </div>

      {/* Report Issue Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Report an Issue</h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Subject</label>
                <input
                  type="text"
                  value={reportData.subject}
                  onChange={(e) => setReportData({ ...reportData, subject: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Brief description of the issue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  value={reportData.message}
                  onChange={(e) => setReportData({ ...reportData, message: e.target.value })}
                  rows="5"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Describe the issue in detail..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReportIssue}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
